import { useQuery } from '@tanstack/react-query'
import { api, type ActivityEvent, type HeadToHead, type ResultEvent } from '../../api'
import { Term } from '../Term'
import { DataTable } from '../ui/DataTable'
import type { TermId } from '../../glossary'
import { marketLabel } from '../../markets'
import { relativeTime } from '../../time'

/**
 * What the league has been doing, newest first.
 *
 * A standings table moves in silence. Somebody who looks once a day sees that they have slipped two
 * places and never sees what did it — which is the half of a betting league people actually talk
 * about. This is the only place on the site that reports somebody else being wrong.
 *
 * The sentence is written here rather than on the server, because only this side knows the reader's
 * timezone and how much width it has. What arrives is facts.
 *
 * Wins and losses take the OUTCOME colours, deliberately: this is a settled result, which is exactly
 * what that scale is for. A placement is neither, so it takes neither — colouring it would put a
 * third meaning on a two-value scale.
 */
function ActivityFeed({
  events,
  results,
}: {
  events: ActivityEvent[]
  results: ResultEvent[]
}) {
  // Nothing at all until somebody bets. A panel headed "recent activity" over an empty box on a
  // season that has not started is furniture, and the same judgement HeadToHeadRecord makes.
  if (events.length === 0 && results.length === 0) return null

  /*
   * Bets and results in one column, ordered by when each happened.
   *
   * They arrive as two lists because they are two shapes — one is bet-shaped and one is not — and
   * merging them here rather than on the server is what lets a result sit directly above the bets
   * it settled. "BoomBoys won game 3" followed by three payouts reads as a sequence; the payouts
   * alone read as gold appearing for no reason, which is what this panel looked like before.
   *
   * A result is stamped at the map's END and a settlement some minutes later, so the natural
   * ordering already puts them the right way round.
   */
  const feed = [
    ...results.map((r) => ({ at: r.at, result: r, bet: null as ActivityEvent | null })),
    ...events.map((e) => ({ at: e.at, result: null as ResultEvent | null, bet: e })),
  ].sort((left, right) => right.at - left.at)

  return (
    <section className="min-w-0 space-y-2">
      <h3 className="text-xs uppercase tracking-wide text-ink-faint">Lately</h3>
      <ul className="min-w-0 space-y-1.5">
        {feed.map((row, index) =>
          row.result ? (
            <li
              key={`result-${row.at}-${index}`}
              className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-sm"
            >
              <MatchResult result={row.result} />
              <span className="tabular ml-auto shrink-0 text-xs text-ink-faint">
                {relativeTime(row.at)}
              </span>
            </li>
          ) : (
            <li
              // Two events of one bet share every other field, so the key has to carry both the
              // instant and what happened at it.
              key={`${row.bet!.at}-${row.bet!.kind}-${row.bet!.name}-${index}`}
              className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-sm"
            >
              <span className={row.bet!.isYou ? 'font-medium text-accent' : 'text-ink'}>
                {row.bet!.isYou ? 'You' : row.bet!.name}
              </span>
              <ActivityVerb event={row.bet!} />
              <span className="text-ink-soft">
                {row.bet!.side === 'A' ? row.bet!.teamAName : row.bet!.teamBName}
              </span>
              <span className="text-ink-faint">{marketLabel(row.bet!.market).toLowerCase()}</span>
              <span className="tabular ml-auto shrink-0 text-xs text-ink-faint">
                {relativeTime(row.bet!.at)}
              </span>
            </li>
          ),
        )}
      </ul>
    </section>
  )
}

/**
 * The sentence a finished map makes, composed here from the facts the server sent.
 *
 * Both halves when the map ended the series, because they are two different pieces of news and the
 * bigger one is the series: "TEAM VISION won game 3" and "took the series 2-1" are worth saying
 * together exactly once, at the moment both became true.
 */
function MatchResult({ result }: { result: ResultEvent }) {
  const winner = result.winner === 'A' ? result.teamAName : result.teamBName

  return (
    <>
      <span className="text-ink">{winner}</span>
      <span className="text-ink-soft">won game {result.gameNumber}</span>
      <span className="text-ink-faint">
        v {result.winner === 'A' ? result.teamBName : result.teamAName}
      </span>
      {result.decided ? (
        <span className="rounded bg-surface px-1.5 py-0.5 text-xs uppercase tracking-wide text-ink-soft">
          series {Math.max(result.scoreA, result.scoreB)}&ndash;
          {Math.min(result.scoreA, result.scoreB)}
        </span>
      ) : (
        <span className="tabular text-xs text-ink-faint">
          {result.scoreA}&ndash;{result.scoreB}
        </span>
      )}
    </>
  )
}

/** The middle of the sentence, which is where the whole outcome lives. */
function ActivityVerb({ event }: { event: ActivityEvent }) {
  if (event.kind === 'placed') {
    return (
      <span className="text-ink-soft">
        backed <span className="tabular text-ink">{event.stake.toLocaleString()}</span> on
      </span>
    )
  }

  if (event.kind === 'void') {
    // A map the series never reached. The stake came back, so this is neither a win nor a loss and
    // must not be painted as one -- the four-settlement rule, on screen.
    //
    // The possessive has to agree with the subject, which is "You" on the reader's own rows and a
    // name on everybody else's. The screenshot read "You got their stake back on Team Resilience",
    // which is the kind of thing that survives a review of the code and not a look at the page.
    return (
      <span className="text-ink-faint">got {event.isYou ? 'your' : 'their'} stake back on</span>
    )
  }

  const won = event.kind === 'won'

  return (
    <span className={won ? 'text-win' : 'text-loss'}>
      {won ? 'won' : 'lost'}{' '}
      <span className="tabular">
        {(won ? (event.returned ?? 0) - event.stake : event.stake).toLocaleString()}
      </span>{' '}
      <span className="text-ink-soft">on</span>
    </span>
  )
}

/**
 * Which house character a bot is, so its row explains itself where it stands.
 *
 * The names are the API's strategy strings; the glossary holds what each one is for.
 */
const BOT_TERMS: Record<string, TermId> = {
  chalk: 'bot-chalk',
  romantic: 'bot-romantic',
  'draft-master': 'bot-draft-master',
  follower: 'bot-follower',
}

/** Gold, with its sign, since a profit column reads wrong without one. */
const signed = (value: number) => `${value > 0 ? '+' : ''}${value.toLocaleString()}`

/**
 * Who the reader has actually beaten, and who has beaten them.
 *
 * A ladder says who is ahead. It does not say that you and one particular person have taken
 * opposite sides four times and that they were right three of them — which is the thing people
 * argue about, and the reason to come back after a match rather than after a season.
 *
 * Nothing is shown until there is a contest to report. An empty row headed "head to head" on a
 * board where nobody has disagreed yet is furniture.
 */
function HeadToHeadRecord({ record }: { record: HeadToHead[] }) {
  if (record.length === 0) return null

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-soft">
      <span className="text-ink-faint">Head to head</span>
      {record.map((against) => (
        <span key={against.userId} className="tabular flex items-baseline gap-1.5">
          <span className="truncate text-ink">{against.name}</span>
          <span className={against.won > against.lost ? 'text-accent' : 'text-ink-faint'}>
            {against.won}&ndash;{against.lost}
          </span>
        </span>
      ))}
    </div>
  )
}

/** Marks the reader's own row, wherever they happen to sit. */
function YouTag() {
  return (
    <span className="shrink-0 rounded-sm border border-accent-edge bg-accent-bg px-1 text-[0.65rem] uppercase tracking-wide text-accent">
      you
    </span>
  )
}

/**
 * Closing-line value, in probability points.
 *
 * Rounded before the sign is chosen, not after. A movement of -0.00004 rounds to zero and kept its
 * minus on the way, so the column read "-0.0pp" — a figure that says the line moved against
 * somebody by nothing at all.
 */
function points(value: number) {
  const rounded = Math.round(value * 1000) / 10
  return `${rounded > 0 ? '+' : ''}${rounded.toFixed(1)}pp`
}

/** Green for a profit, red for a loss, quiet for nothing yet. */
const profitTone = (profit: number) =>
  profit > 0 ? 'text-accent' : profit < 0 ? 'text-danger' : 'text-ink-faint'

export function Leaderboard({ leagueId, seasonId }: { leagueId: number; seasonId: number }) {
  const { data, isPending, error } = useQuery({
    queryKey: ['fantasy', 'leaderboard', leagueId, seasonId],
    queryFn: () => api.leaderboard(leagueId, seasonId),
    refetchInterval: 60_000,
  })

  if (isPending) return <p className="text-sm text-ink-faint">Loading…</p>
  if (error) {
    return (
      <p className="rounded border border-danger-edge bg-danger-bg px-4 py-3 text-sm text-danger">
        {error instanceof Error ? error.message : 'Could not load the leaderboard.'}
      </p>
    )
  }

  return (
    <div className="min-w-0 space-y-3">
      <p className="max-w-3xl text-sm text-ink-soft">
        The skill ladder: who reads team strength and draft power best.
      </p>

      {data.note !== null ? (
        <p className="max-w-3xl rounded border border-accent-edge bg-accent-bg px-3 py-2 text-xs text-accent">
          {data.note}
        </p>
      ) : null}

      <HeadToHeadRecord record={data.yourRecord} />

      <DataTable
        rows={data.standings}
        keyOf={(row) => row.userId}
        columns={[
          { head: '#', cell: (row) => row.rank, card: 'index' },
          {
            head: 'Who',
            card: 'title',
            cell: (row) => (
              <span className="flex min-w-0 items-center gap-2">
                {row.isBot && row.strategy !== null && BOT_TERMS[row.strategy] !== undefined ? (
                  <Term id={BOT_TERMS[row.strategy]}>{row.name}</Term>
                ) : (
                  <span className="truncate">{row.name}</span>
                )}
                {/*
                  Which row is the reader. A ladder with no "you" on it is a table of strangers —
                  the reader has to find their own handle before the ranking means anything, and on
                  a long board they scan for it every time.

                  Accent, not an outcome colour: this is state, and green here would read as being
                  up on the season.
                */}
                {row.isYou ? <YouTag /> : null}
              </span>
            ),
          },
          {
            head: 'Gold',
            align: 'right',
            card: 'lead',
            cell: (row) => row.bankroll.toLocaleString(),
          },
          {
            head: 'Profit',
            align: 'right',
            cell: (row) => signed(row.profit),
            className: (row) => profitTone(row.profit),
          },
          // Beside the ranking, never inside it. Deducting open stakes from the bankroll is what
          // made placing a bet look like losing one.
          {
            head: 'Riding',
            align: 'right',
            cell: (row) => (row.atRisk > 0 ? `${row.atRisk.toLocaleString()} · ${row.open}` : '—'),
          },
          // Mandatory, not optional: Market Man can only bet where a real price was bought, so
          // without this column its row reads as broken rather than as designed.
          { head: 'Bets', align: 'right', cell: (row) => row.bets },
        ]}
      />

      {/*
        Under the table rather than over it. The table answers "where do I stand", which is what
        somebody opening this tab came for; the feed answers "what moved it", which only means
        anything once you have seen the standing it explains.
      */}
      <ActivityFeed events={data.activity} results={data.results} />
    </div>
  )
}

/**
 * The same picks, graded again at real bookmaker prices.
 *
 * Deliberately not a ranking and deliberately not called a bankroll — see the caveats, which come
 * from the server so the page and the arithmetic cannot drift apart.
 */
export function MarketComparison({ leagueId, seasonId }: { leagueId: number; seasonId: number }) {
  const { data, isPending, error } = useQuery({
    queryKey: ['fantasy', 'market', leagueId, seasonId],
    queryFn: () => api.marketComparison(leagueId, seasonId),
  })

  if (isPending) return <p className="text-sm text-ink-faint">Loading…</p>
  if (error) {
    return (
      <p className="rounded border border-danger-edge bg-danger-bg px-4 py-3 text-sm text-danger">
        {error instanceof Error ? error.message : 'Could not load the comparison.'}
      </p>
    )
  }

  return (
    <div className="min-w-0 space-y-3">
      <p className="max-w-3xl text-sm text-ink-soft">
        These same picks, at the prices a real bookmaker was offering, plus their{' '}
        <Term id="closing-line" define />.
      </p>

      <p className="tabular text-xs text-ink-faint">
        {data.coverage.comparedBets} of {data.coverage.settledBets} settled bets had a real price to
        compare against, across {data.coverage.fixturesWithAPrice} of {data.coverage.fixtures}{' '}
        fixtures.
      </p>

      {data.caveats.map((caveat) => (
        <p key={caveat} className="max-w-3xl text-xs text-ink-faint">
          {caveat}
        </p>
      ))}

      {data.standings.length === 0 ? (
        <p className="rounded border border-line bg-surface px-4 py-8 text-center text-sm text-ink-soft">
          No bet in this season landed on a fixture we bought a real price for. That is the ordinary
          state against a 250-call month — it is not a statement about anybody&rsquo;s picks.
        </p>
      ) : (
        <DataTable
          rows={data.standings}
          keyOf={(row) => row.userId}
          columns={[
            {
              head: 'Who',
              card: 'title',
              cell: (row) => <span className="truncate">{row.name}</span>,
            },
            { head: 'Bets', align: 'right', cell: (row) => row.bets },
            { head: 'Here', align: 'right', cell: (row) => signed(row.houseProfit) },
            { head: 'At market', align: 'right', cell: (row) => signed(row.marketProfit) },
            {
              head: 'CLV',
              align: 'right',
              card: 'lead',
              cell: (row) => (row.clvPoints === null ? '—' : points(row.clvPoints)),
              className: (row) => ((row.clvPoints ?? 0) > 0 ? 'text-accent' : 'text-ink-soft'),
            },
          ]}
        />
      )}
    </div>
  )
}

/** The all-time table for one fantasy league. Finished seasons only. */
export function CareerTable({ leagueId }: { leagueId: number }) {
  const { data, isPending } = useQuery({
    queryKey: ['fantasy', 'career', leagueId],
    queryFn: () => api.career(leagueId),
  })

  if (isPending || data === undefined) return null

  return (
    <section className="min-w-0 space-y-2">
      <h3 className="text-sm font-medium text-ink-soft">Career</h3>
      {data.standings.length === 0 ? (
        <p className="text-xs text-ink-faint">
          {data.note ?? 'A season counts towards this once it is finished.'}
        </p>
      ) : (
        <DataTable
          rows={data.standings}
          keyOf={(row) => row.userId}
          columns={[
            { head: '#', cell: (row) => row.rank, card: 'index' },
            {
              head: 'Who',
              card: 'title',
              cell: (row) => <span className="truncate">{row.name}</span>,
            },
            {
              head: 'Profit',
              align: 'right',
              card: 'lead',
              cell: (row) => signed(row.profit),
              className: (row) => profitTone(row.profit),
            },
            { head: 'Seasons', align: 'right', cell: (row) => row.seasons },
            { head: 'Bets', align: 'right', cell: (row) => row.bets },
          ]}
        />
      )}
    </section>
  )
}
